import React, { useEffect, useState, useCallback,useRef } from "react";
import AxiosInstance from "../AxiosInstance";
import { FaTrash } from "react-icons/fa";
import { useLocation ,useNavigate} from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { ArrowLeftCircle } from "react-bootstrap-icons";
import ToastNotification from "../ToastNotification";
import Layout from "./Layout";
const DocumentUpload = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userId = location.state?.userId || localStorage.getItem("userId");
  const toastRef=useRef();
  const [documents, setDocuments] = useState([]); 
  const [documentTypes, setDocumentTypes] = useState([]); 
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState(""); // Track selected document type
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocId, setSelectedDocId] = useState(null);

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const res = await AxiosInstance.get("/admin/get-document-types");
        setDocumentTypes(res.data);
      } catch (err) {
        const message = err.response?.data?.error || "Failed to fetch document types.";
        toastRef.current?.showToast(message, "danger");
      }
    };

    fetchDocumentTypes();
  }, []);

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      try {
        const res = await AxiosInstance.get(`/common/api/user-details/${userId}`);
        setEmployeeDetails(res.data);
      } catch (err) {
        const message = err.response?.data?.error || "Failed to fetch employee details.";
        toastRef.current?.showToast(message, "danger");
      }
    };

    if (userId) {
      fetchEmployeeDetails();
    }
  }, [userId]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await AxiosInstance.get(`/admin/get-user-documents/${userId}`);
      setDocuments(
        res.data.map((doc) => ({
          id: doc.id,
          filename: doc.filename,
          uploaded: doc.uploadedAt !== null,
          filePath: doc.filePath,
          type: doc.documentType,
        }))
      );
    } catch (err) {
      if (err.response?.status === 404) {
        console.log("No documents found for this user.");
        setDocuments([]); // Set empty so UI still loads
      } else {
        const message = err.response?.data?.error || "Failed to fetch documents.";
        toastRef.current?.showToast(message, "danger");
      }
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchDocuments();
    }
  }, [userId, fetchDocuments]);

  const formatValue = (value) => {
    return value ? value : "N/A";
  };
  

  const handleOpenModal = (id, docType) => {
    setSelectedDocId(id);
    setSelectedDocType(docType || ""); // Set document type if already available
    setShowModal(true);
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUploadConfirm = async () => {
  if (!selectedFile || !selectedDocType) {
    toastRef.current.showToast("Please select both a document type and a file before uploading.", "danger");
    return;
  }

  const documentTypeId = documentTypes.find((dt) => dt.name === selectedDocType)?.id;
    if (!documentTypeId) {
      toastRef.current?.showToast("Invalid document type selected.", "danger");
      return;
    }

   const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("documentTypeId", documentTypeId); // Send ID instead of name

    try {
      await AxiosInstance.post(`/admin/upload-document/${userId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toastRef.current?.showToast("Document uploaded successfully!", "success");

      // Update UI
      setDocuments((prevDocs) =>
        prevDocs.map((doc) =>
          doc.id === selectedDocId ? { ...doc, uploaded: true, type: selectedDocType } : doc
        )
      );

    setShowModal(false);
    await fetchDocuments(); // Refresh list
  } catch (error) {
    const message = error.response?.data?.error || "Failed to upload document.";
    toastRef.current?.showToast(message, "danger");
  }
};

const handleDownload = async (id) => {
  try {
    const response = await AxiosInstance.get(`/admin/download-document/${id}`, {
      responseType: "blob",
    });

    // Create a blob URL
    const url = window.URL.createObjectURL(new Blob([response.data]));

    // Create a temporary anchor element to trigger download
    const a = document.createElement("a");
    a.href = url;
    a.download = "document.pdf"; // Can be dynamic if needed
    document.body.appendChild(a);
    a.click();

    // Cleanup
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    toastRef.current?.showToast("Download started successfully!", "success");
  } catch (error) {
    const message = error.response?.data?.error || "Error downloading file";
    toastRef.current?.showToast(message, "danger");
  }
};

const handleDelete = async (id) => {
  if (!id) {
    toastRef.current?.showToast("Invalid document ID.", "danger");
    return;
  }

  try {
    await AxiosInstance.delete(`/admin/delete-document/${id}`);
    toastRef.current?.showToast("Document deleted successfully!", "success");
    setDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== id));
  } catch (err) {
    const message = err.response?.data?.error || "Failed to delete document.";
    toastRef.current?.showToast(message, "danger");
  }
};

const handleAddRow = (docType) => {
  if (!docType) {
    toastRef.current.showToast("Please select a document type first!", "warning");
    return;
  }

  setDocuments((prevDocs) => [
    ...prevDocs,
    {
      id: Date.now(),
      filename: "",
      type: docType, // Use the passed document type
      uploaded: false,
      filePath: null,
    },
  ]);

  toastRef.current.showToast("New row added successfully!", "success");
};

const capitalizeWords = (str) => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}; 

  return (
    <Layout heading="Documents">
      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between mt-4 mb-3">
          <div className="d-flex align-items-center gap-3">
            <Button className="btn btn-secondary" onClick={() => navigate(-1)}>
              <ArrowLeftCircle />
            </Button>
            {employeeDetails && (
              <div>
                <h6 className="fw-bold mb-0">
                  {capitalizeWords(`${formatValue(employeeDetails.first_name)} ${employeeDetails.middle_name ? formatValue(employeeDetails.middle_name) + ' ' : ''}${formatValue(employeeDetails.last_name)}`)}
                </h6>
                <small className="text-muted">
                  {formatValue(employeeDetails.employee_code)}
                </small>
              </div>
            )}
          </div>
        </div>
        <div className="container mt-4">
  {/* Responsive Table Wrapper */}
  <div className="table-responsive">
    <table className="table table-hover text-center align-middle table-striped">
      <thead className="table-primary">
        <tr>
          <th scope="col">#</th>
          <th scope="col" className="text-start">Document Name</th>
          <th scope="col" className="text-start">Document Type</th>
          <th scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {documents.map((doc, index) => (
          <tr key={doc.id}>
            <td>{index + 1}</td>
            <td className="text-start text-truncate" style={{ maxWidth: "150px" }}>
              {doc.filename}
            </td>
            <td className="text-start">{doc.type || "N/A"}</td>
            <td className="d-flex flex-wrap justify-content-center">
              <button
                className="btn btn-primary btn-sm me-2"
                onClick={() => handleOpenModal(doc.id, doc.type)}
                disabled={doc.uploaded}
              >
                <i className="bi bi-upload"></i>
              </button>
              {doc.uploaded && (
                <button className="btn btn-sm btn-primary me-2" onClick={() => handleDownload(doc.id)}>
                  <i className="bi bi-download"></i>
                </button>
              )}
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(doc.id)}>
                <FaTrash />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>

  {/* Dropdown for Adding Document */}
  <div className="d-flex justify-content-end">
  <div className="dropdown">
    <button
      className="btn btn-primary dropdown-toggle mb-3"
      type="button"
      id="dropdownMenuButton"
      data-bs-toggle="dropdown"
      aria-expanded="false"
    >
      Add Document
    </button>
    <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
      {documentTypes.map((doc) => (
        <li key={doc.id}>
          <button className="dropdown-item" onClick={() => handleAddRow(doc.name)}>
            {doc.name}
          </button>
        </li>
      ))}
    </ul>
  </div>
</div>
</div>
  {/* Upload Modal */}
  <Modal show={showModal} onHide={() => setShowModal(false)}>
    <Modal.Header closeButton>
      <Modal.Title>Upload Document</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <input type="file" className="form-control" onChange={handleFileChange} />
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={() => setShowModal(false)}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleUploadConfirm}>
        Upload
      </Button>
    </Modal.Footer>
  </Modal>
  <ToastNotification ref={toastRef} />
</div>
</Layout>
  );
};

export default DocumentUpload;
